import type { JSX } from "react";
import type { BikeTypeV2 } from "@jobrad-gmbh/bike-leasing-calculator";
import { FormField, FormFieldWithFieldset } from "../FormField";
import { NumericInput } from "../NumericInput";
import { SimpleRadioGroup } from "../SimpleRadioGroup";
import { BikeTypeSelect } from "../BikeTypeSelect";
import { useBudgetCalculator } from "./useBudgetCalculator";
import styles from "./BudgetCalculator.module.css";

const BudgetCalculator = (): JSX.Element => {
  const {
    mergedInput,
    elementVisibility,
    bikeTypeOptions,
    serviceOptions,
    serviceTypeOptions,
    currentServiceValue,
    insurancePayerOptions,
    budgetExceedsMax,
    budgetErrorMessage,
    grantExceedsMax,
    grantErrorMessage,
    handleBudgetChange,
    handleServiceChange,
    handleInsurancePayerChange,
    handleGrantChange,
    handleBikeTypeChange,
  } = useBudgetCalculator();

  const {
    insurancePayerSelect: showInsurancePayerSelect,
    grantInput: showGrantInput,
    bikeTypeSelect: showBikeTypeSelect,
    serviceSelect: showServiceSelect,
  } = elementVisibility;

  return (
    <div className={styles.budgetCalculator}>
      <div className={styles.form}>
        <FormField id="budgetMonthlyBudget" label="budgetCalculator.monthlyBudget" errorMessage={budgetErrorMessage}>
          <NumericInput
            value={mergedInput.monthlyBudgetBrutto}
            onValueChanged={handleBudgetChange}
            decimalScale={2}
            hasError={budgetExceedsMax}
            aria-labelledby="budgetMonthlyBudget_label"
          />
        </FormField>

        {showBikeTypeSelect && (
          <FormFieldWithFieldset id="budgetBikeType" label="bikeTypeLabel">
            <BikeTypeSelect
              availableBikeTypes={bikeTypeOptions}
              selectedBikeType={(mergedInput.fahrradtyp as BikeTypeV2) ?? "e_bike"}
              onValueChanged={handleBikeTypeChange}
              ariaLabelledBy="budgetBikeType_label"
            />
          </FormFieldWithFieldset>
        )}

        {showServiceSelect && (
          <FormFieldWithFieldset id="budgetServiceSelect" label="budgetCalculator.service">
            <SimpleRadioGroup
              options={serviceTypeOptions}
              variant="toggleButton"
              onValueChangedParse={(newSelectionId) => {
                const selected = serviceOptions.find((s) => s.id === Number.parseInt(newSelectionId));
                handleServiceChange(selected);
              }}
              name="budgetServiceSelect"
              value={currentServiceValue?.id.toString()}
              ariaLabelledBy="budgetServiceSelect_label"
            />
          </FormFieldWithFieldset>
        )}

        {showInsurancePayerSelect && (
          <FormFieldWithFieldset id="budgetInsurancePayer" label="budgetCalculator.insurancePayer">
            <SimpleRadioGroup
              options={insurancePayerOptions}
              variant="toggleButton"
              onValueChangedParse={handleInsurancePayerChange}
              name="budgetInsurancePayer"
              value={mergedInput.versicherungsZahler}
              ariaLabelledBy="budgetInsurancePayer_label"
            />
          </FormFieldWithFieldset>
        )}

        {showGrantInput && (
          <FormField id="budgetEmployerGrant" label="budgetCalculator.employerGrant" errorMessage={grantErrorMessage}>
            <NumericInput
              value={mergedInput.arbeitgeberZuschuss}
              onValueChanged={handleGrantChange}
              decimalScale={2}
              hasError={grantExceedsMax}
              aria-labelledby="budgetEmployerGrant_label"
            />
          </FormField>
        )}
      </div>
    </div>
  );
};

export { BudgetCalculator };
